// import { Download } from "lucide-react";
// import React from "react";
// import * as XLSX from "xlsx-js-style";
// import { useParams } from "react-router-dom";

// function ExportLinks({ data }) {
//   const { tablename } = useParams();

//   const exportExcel = () => {
//     if (!data || data.length === 0) return alert("No data to export.");

//     // === Filtered flatten logic: only include .name if nested ===
//     const processedData = data.map((item) => filterObject(item));

//     // Collect keys from processed data
//     const keys = Array.from(
//       new Set(processedData.flatMap((row) => Object.keys(row)))
//     );

//     // Format headers (capitalize)
//     const headers = keys.map((key) =>
//       key.replaceAll("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())
//     );

//     // Build worksheet
//     const wsData = [
//       headers,
//       ...processedData.map((row) => keys.map((k) => row[k])),
//     ];
//     const ws = XLSX.utils.aoa_to_sheet(wsData);

//     // Auto column width
//     const colWidths = headers.map((h, i) => {
//       const maxLength = Math.max(
//         h.length,
//         ...processedData.map((r) =>
//           r[keys[i]] ? r[keys[i]].toString().length : 0
//         )
//       );
//       return { wch: maxLength + 2 };
//     });
//     ws["!cols"] = colWidths;

//     // Header styling
//     const range = XLSX.utils.decode_range(ws["!ref"]);
//     for (let C = range.s.c; C <= range.e.c; ++C) {
//       const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
//       const cell = ws[cellAddress];
//       if (cell) {
//         cell.s = {
//           fill: { fgColor: { rgb: "2563EB" } }, // Tailwind blue-600
//           font: { bold: true, color: { rgb: "FFFFFF" } },
//           alignment: { horizontal: "center", vertical: "center" },
//           border: {
//             top: { style: "thin", color: { rgb: "DDDDDD" } },
//             bottom: { style: "thin", color: { rgb: "DDDDDD" } },
//           },
//         };
//       }
//     }

//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
//     XLSX.writeFile(wb, `${tablename || "export"}.xlsx`);
//   };

//   // === Helper: Flatten but only include .name (or .title fallback) for nested objects ===
//   const filterObject = (obj, res = {}, prefix = "") => {
//     for (let key in obj) {
//       const value = obj[key];
//       const fullKey = prefix ? `${prefix}_${key}` : key;

//       if (typeof value === "object" && value !== null) {
//         // Only take 'name' (or 'title' if name missing)
//         if (value.name) {
//           res[`${key}_name`] = value.name;
//         } else if (value.title) {
//           res[`${key}_name`] = value.title;
//         }
//       } else {
//         res[fullKey] = value;
//       }
//     }
//     return res;
//   };

//   return (
//     <nav aria-label="Page navigation example" className="my-2">
//       <ul className="flex justify-center space-x-4">
//         <li>
//           <button
//             onClick={exportExcel}
//             className="text-blue-500 hover:text-blue-600 flex items-center space-x-2"
//           >
//             <Download className="w-4 h-4" />
//             <span>Export</span>
//           </button>
//         </li>
//       </ul>
//     </nav>
//   );
// }

// export default ExportLinks;

import { Download } from "lucide-react";
import React from "react";
import * as XLSX from "xlsx-js-style";
import { useParams } from "react-router-dom";

function ExportLinks({ data }) {
  const { tablename } = useParams();

  const exportExcel = () => {
    if (!data || data.length === 0) return alert("No data to export.");

    // === Flatten and filter (only .name or .title for nested) ===
    const processedData = data.map((item) => filterObject(item));

    // === Format all date-like fields ===
    const formattedData = processedData.map((row) => {
      const formatted = {};
      for (const [key, value] of Object.entries(row)) {
        if (isDateString(value)) {
          formatted[key] = new Date(value).toLocaleString(); // ✅ Local date/time
        } else {
          formatted[key] = value;
        }
      }
      return formatted;
    });

    // === Collect all headers ===
    const keys = Array.from(
      new Set(formattedData.flatMap((r) => Object.keys(r)))
    );
    const headers = keys.map((key) =>
      key
        .replaceAll("_", " ")
        .replace(/\bname\b/gi, "") // remove redundant "_name"
        .trim()
        .replace(/\b\w/g, (l) => l.toUpperCase())
    );

    // === Build worksheet ===
    const wsData = [
      headers,
      ...formattedData.map((r) => keys.map((k) => r[k])),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // === Auto column width ===
    const colWidths = headers.map((h, i) => {
      const maxLength = Math.max(
        h.length,
        ...formattedData.map((r) =>
          r[keys[i]] ? r[keys[i]].toString().length : 0
        )
      );
      return { wch: maxLength + 2 };
    });
    ws["!cols"] = colWidths;

    // === Header styling ===
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      const cell = ws[cellAddress];
      if (cell) {
        cell.s = {
          fill: { fgColor: { rgb: "2563EB" } }, // Tailwind blue-600
          font: { bold: true, color: { rgb: "FFFFFF" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "DDDDDD" } },
            bottom: { style: "thin", color: { rgb: "DDDDDD" } },
          },
        };
      }
    }

    // === Export workbook ===
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${tablename || "export"}.xlsx`);
  };

  // === Helper: flatten but only take .name or .title ===
  const filterObject = (obj, res = {}, prefix = "") => {
    for (let key in obj) {
      const value = obj[key];
      const fullKey = prefix ? `${prefix}_${key}` : key;

      if (typeof value === "object" && value !== null) {
        if (value.name) {
          res[`${key}_name`] = value.name;
        } else if (value.fullname) {
          res[`${key}_name`] = value.fullname;
        } else if (value.title) {
          res[`${key}_name`] = value.title;
        }
      } else {
        res[fullKey] = value;
      }
    }
    return res;
  };

  // === Helper: detect ISO date-like strings ===
  const isDateString = (val) => {
    return (
      typeof val === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val) &&
      !isNaN(Date.parse(val))
    );
  };

  return (
    <nav aria-label="Page navigation example" className="my-2">
      <ul className="flex justify-center space-x-4">
        <li>
          <button
            onClick={exportExcel}
            className="text-blue-500 hover:text-blue-600 flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default ExportLinks;
